import { useEffect, useRef, useState } from 'react';

import {
  IconAlertOctagonFilled,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Card, Select, TextInput } from '@cfa/react-core';

import { useBreakpointUp } from '@/hooks/useMediaQuery';

import { cn } from '@/utils/tailwind';
import { validateTextField } from '@/utils/validateTextField';

import ConfirmationModal from 'components/popups/ConfirmationModal';
import Constants from 'constants/index';

import {
  createEmptyMultipleChoiceQuestion,
  createEmptyTrueFalseQuestion,
  oppositeLanguage,
  patchQuestionPrompt,
  type QuizLocale,
  replaceQuestionKeepingPrompt,
  type SetV2QuizEditorState,
  type V2QuizEditorState,
} from '../state';
import { MAX_V2_QUIZ_TEXT_LENGTH, translateText } from '../translateText';
import { isTrueFalseQuestion } from '../v2QuizValidation';

import BuildQuizzesAnswersV2 from './BuildQuizzesAnswersV2';
import CollapsedQuestionPreview from './CollapsedQuestionPreview';

import SixDotIcon from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/SixDotIcon';

const questionTemplateTypesArray = Object.values(
  Constants.QUESTION_TEMPLATE_TYPES,
);

const trashButtonClassName =
  'text-grayscale-gray6 cursor-pointer transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 [@media(hover:hover)]:pointer-events-none [@media(hover:hover)]:opacity-0';

/**
 * Blur schedules a missing-correct-answer check; radio click in the same gesture
 * must win first. `0` can paint the error (shifting radios) before `click`.
 */
const CORRECT_ANSWER_ERROR_CHECK_DELAY_MS = 150;

interface BuildQuizzesQuestionCardV2Props {
  questionIndex: number;
  quiz: V2QuizEditorState;
  setQuiz: SetV2QuizEditorState;
  /** userPrefs language — not `quiz.primaryLanguage`. */
  userLanguage: QuizLocale;
  isSaving: boolean;
  validationMode: boolean;
  setValidationMode: (value: boolean) => void;
  trySave: (state: V2QuizEditorState, questionIndex: number | null) => void;
  registerTranslate: (key: string, abort: AbortController) => void;
  clearTranslate: (key: string, abort: AbortController) => void;
  handleRef?: React.Ref<HTMLElement>;
  canReorder?: boolean;
  isDragSource?: boolean;
  isCollapsed?: boolean;
  isAnyDragActive?: boolean;
  isDropTarget?: boolean;
  moveQuestionUp?: (questionIndex: number) => void;
  moveQuestionDown?: (questionIndex: number) => void;
  /** Read-only: controls are disabled, and disabled inputs get no mouse events, so overlays carry the hover `title`. */
  isDisplayLanguageMismatch?: boolean;
  isDraft?: boolean;
  onDiscardDraft?: () => void;
}

const BuildQuizzesQuestionCardV2: React.FC<BuildQuizzesQuestionCardV2Props> = ({
  questionIndex,
  quiz,
  setQuiz,
  userLanguage,
  isSaving,
  validationMode,
  setValidationMode,
  trySave,
  registerTranslate,
  clearTranslate,
  handleRef,
  canReorder = false,
  isDragSource = false,
  isCollapsed = false,
  isAnyDragActive = false,
  isDropTarget = false,
  moveQuestionUp,
  moveQuestionDown,
  isDisplayLanguageMismatch = false,
  isDraft = false,
  onDiscardDraft,
}) => {
  const { t } = useTranslation();
  // Mobile and desktop each render their own reorder handlegrip. A React ref can only point
  // at one node, so only mount the grip for the active breakpoint (ordering later).
  const isMdAndUp = useBreakpointUp('md');
  const [showDeleteQuestionPopup, setShowDeleteQuestionPopup] = useState(false);
  const [isReorderAnimating, setIsReorderAnimating] = useState(false);
  // Field-level: show prompt error only after this input was blurred (or leave).
  const [promptTouched, setPromptTouched] = useState(false);
  // Show "select correct answer" after blurring a field while none is chosen.
  const [showCorrectAnswerError, setShowCorrectAnswerError] = useState(false);
  // Blur runs before radio click — defer the check so selecting correct can win.
  const correctAnswerErrorTimeoutRef = useRef<number | null>(null);
  // After `await`, setState updaters are not guaranteed to run before we read
  // `nextForSave` — keep a snapshot for post-translate merges.
  const quizRef = useRef(quiz);

  useEffect(() => {
    quizRef.current = quiz;
  }, [quiz]);

  useEffect(
    () => () => {
      if (correctAnswerErrorTimeoutRef.current != null) {
        window.clearTimeout(correctAnswerErrorTimeoutRef.current);
      }
    },
    [],
  );

  const question = quiz.questions[questionIndex];
  const isTrueFalse = question ? isTrueFalseQuestion(question) : false;
  const questionTemplateType = isTrueFalse
    ? Constants.QUESTION_TEMPLATE_TYPES.TRUE_OR_FALSE
    : Constants.QUESTION_TEMPLATE_TYPES.MULTIPLE_CHOICE;

  const correctOptionSelected =
    question?.answers.some(option => option.correct === true) ?? false;
  const questionNumber = questionIndex + 1;
  const hasMultipleQuestions = quiz.questions.length > 1;
  const isLastQuestion = questionIndex === quiz.questions.length - 1;
  const showReorderControls = hasMultipleQuestions && canReorder;
  const showDeleteTrash = isDraft || showReorderControls;
  const draftTrashClassName = 'text-grayscale-gray6 shrink-0 cursor-pointer';
  const deleteTrashClassName = !showDeleteTrash
    ? 'text-grayscale-gray6 pointer-events-none opacity-0'
    : isDraft
      ? draftTrashClassName
      : trashButtonClassName;
  const isCollapsedState = isAnyDragActive || isCollapsed;

  const handleMoveUp = () => {
    if (!canReorder) {
      return;
    }
    setIsReorderAnimating(true);
    moveQuestionUp?.(questionIndex);
    setTimeout(() => setIsReorderAnimating(false), 200);
  };

  const handleMoveDown = () => {
    if (!canReorder) {
      return;
    }
    setIsReorderAnimating(true);
    moveQuestionDown?.(questionIndex);
    setTimeout(() => setIsReorderAnimating(false), 200);
  };

  const handleQuestionName = (value: string) => {
    const other = oppositeLanguage(userLanguage);
    setQuiz(prev =>
      patchQuestionPrompt(prev, questionIndex, {
        [userLanguage]: value,
        [other]: '',
      }),
    );
  };

  const clearCorrectAnswerError = () => {
    if (correctAnswerErrorTimeoutRef.current != null) {
      window.clearTimeout(correctAnswerErrorTimeoutRef.current);
      correctAnswerErrorTimeoutRef.current = null;
    }
    setShowCorrectAnswerError(false);
  };

  const markCorrectAnswerErrorIfNeeded = (
    state: V2QuizEditorState,
    index: number,
  ) => {
    const current = state.questions[index];
    if (!current) {
      return;
    }
    // Only after other primary fields are filled — don't compete with empty prompt/answers.
    if (!validateTextField(current.question[userLanguage] ?? '')) {
      return;
    }
    if (
      !isTrueFalseQuestion(current) &&
      current.answers.some(
        answer => !validateTextField(answer.answer[userLanguage] ?? ''),
      )
    ) {
      return;
    }
    if (!current.answers.some(answer => answer.correct === true)) {
      setShowCorrectAnswerError(true);
    }
  };

  /**
   * Defer past blur → radio click in the same gesture. `setTimeout(0)` can paint
   * the error (shifting radios under the cursor) before `click`/`change` lands;
   * `onCorrectAnswerSelected` clears this timer when selection wins.
   */
  const scheduleCorrectAnswerErrorCheck = (index: number) => {
    if (correctAnswerErrorTimeoutRef.current != null) {
      window.clearTimeout(correctAnswerErrorTimeoutRef.current);
    }
    correctAnswerErrorTimeoutRef.current = window.setTimeout(() => {
      correctAnswerErrorTimeoutRef.current = null;
      markCorrectAnswerErrorIfNeeded(quizRef.current, index);
    }, CORRECT_ANSWER_ERROR_CHECK_DELAY_MS);
  };

  const handleQuestionBlur = async (rawValue: string) => {
    const clientId = quiz.questions[questionIndex]?.clientId;
    setPromptTouched(true);

    const primary = rawValue.trim();
    const other = oppositeLanguage(userLanguage);

    // If the value is empty, set the question to empty and return. Don't save an empty question.
    if (!primary) {
      if (clientId) {
        const translateKey = `prompt:${clientId}`;
        const staleAbort = new AbortController();
        staleAbort.abort();
        registerTranslate(translateKey, staleAbort);
        clearTranslate(translateKey, staleAbort);
      }
      const next = patchQuestionPrompt(quizRef.current, questionIndex, {
        [userLanguage]: '',
        [other]: '',
      });
      quizRef.current = next;
      setQuiz(next);
      scheduleCorrectAnswerErrorCheck(questionIndex);
      return;
    }

    if (!clientId) {
      return;
    }

    const translateKey = `prompt:${clientId}`;
    const abort = new AbortController();
    registerTranslate(translateKey, abort);

    try {
      const translated = await translateText(primary, userLanguage).catch(
        () => null,
      );
      if (abort.signal.aborted) {
        return;
      }

      const currentIndex = quizRef.current.questions.findIndex(
        item => item.clientId === clientId,
      );
      if (currentIndex < 0) {
        return;
      }
      // User re-edited or cleared the prompt while this request was in flight.
      const currentPrimary = (
        quizRef.current.questions[currentIndex]?.question[userLanguage] ?? ''
      ).trim();
      if (currentPrimary !== primary) {
        return;
      }

      if (translated == null) {
        // Drop the old translation so it is never saved with the new text;
        // trySave retries the missing translation and surfaces a failure.
        const next = patchQuestionPrompt(quizRef.current, currentIndex, {
          [other]: '',
        });
        quizRef.current = next;
        setQuiz(next);
        clearTranslate(translateKey, abort);
        trySave(next, currentIndex);
        return;
      }

      const nextForSave = patchQuestionPrompt(quizRef.current, currentIndex, {
        [userLanguage]: primary,
        [other]: translated,
      });
      quizRef.current = nextForSave;
      setQuiz(nextForSave);
      clearTranslate(translateKey, abort);
      scheduleCorrectAnswerErrorCheck(currentIndex);
      trySave(nextForSave, currentIndex);
    } finally {
      clearTranslate(translateKey, abort);
    }
  };

  const handleTemplateChange = (templateId: string | null) => {
    if (!templateId || templateId === questionTemplateType.id) return;
    setValidationMode(false);
    setPromptTouched(false);
    clearCorrectAnswerError();
    const replacement =
      templateId === Constants.QUESTION_TEMPLATE_TYPES.MULTIPLE_CHOICE.id
        ? createEmptyMultipleChoiceQuestion()
        : createEmptyTrueFalseQuestion();

    setQuiz(prev =>
      replaceQuestionKeepingPrompt(prev, questionIndex, replacement),
    );
  };

  const deleteQuestion = () => {
    const clientId = quiz.questions[questionIndex]?.clientId;
    const questions = quizRef.current.questions.filter(
      item => item.clientId !== clientId,
    );
    const next = {
      ...quizRef.current,
      questions,
      // Keep threshold ≤ remaining questions so assignees can still pass.
      passingGrade: Math.min(quizRef.current.passingGrade, questions.length),
    };
    quizRef.current = next;
    setQuiz(next);
    setShowDeleteQuestionPopup(false);
    trySave(next, null);
  };

  if (!question) {
    return null;
  }

  const questionNameMissing = !validateTextField(
    question.question[userLanguage] ?? '',
  );
  const showPromptError =
    questionNameMissing && (promptTouched || validationMode);
  const hasEmptyAnswer =
    !isTrueFalse &&
    question.answers.some(
      answer => !validateTextField(answer.answer[userLanguage] ?? ''),
    );
  // Full-question gate (leave / post-blur): empty primary fields, missing correct answer.
  // Translations gate leave/save but are not field-editable here.
  const otherPrimaryFieldsValid = !questionNameMissing && !hasEmptyAnswer;
  const showMissingCorrectAnswer =
    !correctOptionSelected &&
    otherPrimaryFieldsValid &&
    (validationMode || showCorrectAnswerError);
  const hasFullQuestionError =
    (validationMode &&
      (questionNameMissing || !correctOptionSelected || hasEmptyAnswer)) ||
    showMissingCorrectAnswer;

  const gripClassName = cn(
    !hasMultipleQuestions && 'pointer-events-none opacity-0',
    hasMultipleQuestions &&
      !canReorder &&
      'cursor-not-allowed opacity-50 **:data-[testid=Draggable]:cursor-not-allowed',
  );

  const reorderGrip = (position: 'middle' | 'top') => (
    <span
      data-testid="QuestionReorderGrip"
      className={cn(position === 'middle' ? 'pr-3' : '-mt-6', gripClassName)}
    >
      <SixDotIcon position={position} ref={handleRef} />
    </span>
  );

  return (
    <>
      <div>
        <div
          className={cn(
            'grid transition-[grid-template-rows] duration-300 ease-in-out',
            isCollapsedState ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div className="min-h-0 overflow-hidden">
            {isDragSource ? (
              <div className="mb-4 flex h-6 items-center">
                <div className="bg-secondary-base h-2 w-full rounded-full" />
              </div>
            ) : (
              <CollapsedQuestionPreview
                question={question}
                questionIndex={questionIndex}
                userLanguage={userLanguage}
                hasError={hasFullQuestionError}
              />
            )}
          </div>
        </div>
        <div
          className={cn(
            'grid transition-[grid-template-rows] duration-300 ease-in-out',
            isCollapsedState ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
          )}
        >
          <div
            className={cn(
              'min-h-0 overflow-clip',
              !isCollapsedState && '[overflow-clip-margin:6px]',
            )}
          >
            <Card.Root
              outlined
              style={{
                transition:
                  'background-color 750ms, color 750ms, border-color 750ms, box-shadow 300ms',
              }}
              className={cn(
                'group mb-4 overflow-hidden shadow-md!',
                isReorderAnimating && '[box-shadow:none]!',
                isDropTarget &&
                  'bg-secondary-base! [&_*:not(input,textarea,.cfa-select-button)]:text-white!',
                hasFullQuestionError && 'border-feedback-error-base! border-2!',
              )}
              data-testid={`QuestionCardV2-${question.clientId}`}
            >
              <Card.Content>
                <div className="flex items-center justify-between md:hidden">
                  <div className="flex items-center">
                    {!isMdAndUp && reorderGrip('middle')}
                    <div className="text-grayscale-gray6 relative mt-1 flex shrink-0 items-center self-start">
                      <div className="text-grayscale-gray7 bg-outline-2 mr-3 flex size-10 items-center justify-center rounded-full text-lg font-bold">
                        {questionNumber}
                      </div>
                      {showReorderControls &&
                        questionIndex > 0 &&
                        !isAnyDragActive && (
                          <button
                            onClick={handleMoveUp}
                            className="flex cursor-pointer items-center justify-center p-0"
                            type="button"
                            aria-label={`Move question ${questionNumber} up`}
                          >
                            <IconChevronUp size={30} stroke={1} />
                          </button>
                        )}

                      {showReorderControls &&
                        !isLastQuestion &&
                        !isAnyDragActive && (
                          <button
                            onClick={handleMoveDown}
                            className="flex cursor-pointer items-center justify-center p-0"
                            type="button"
                            aria-label={`Move question ${questionNumber} down`}
                          >
                            <IconChevronDown size={30} stroke={1} />
                          </button>
                        )}
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-label={`Delete question ${questionNumber}`}
                    aria-hidden={!showDeleteTrash}
                    disabled={!showDeleteTrash}
                    data-testid="DiscardDraftQuestion"
                    onClick={() =>
                      isDraft
                        ? onDiscardDraft?.()
                        : setShowDeleteQuestionPopup(true)
                    }
                    className={deleteTrashClassName}
                  >
                    <IconTrash size={24} />
                  </button>
                </div>

                <div className="mt-8 flex w-full items-start gap-5 md:gap-4">
                  {isMdAndUp && reorderGrip('top')}
                  <div className="text-grayscale-gray6 relative mt-1 hidden items-center md:flex">
                    {showReorderControls &&
                      questionIndex > 0 &&
                      !isAnyDragActive && (
                        <button
                          onClick={handleMoveUp}
                          className="absolute top-0 left-1/2 flex size-5 -translate-x-1/2 -translate-y-[120%] cursor-pointer items-center justify-center p-0 transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 [@media(hover:hover)]:pointer-events-none [@media(hover:hover)]:opacity-0"
                          type="button"
                          aria-label={`Move question ${questionNumber} up`}
                        >
                          <IconChevronUp size={20} stroke={2} />
                        </button>
                      )}
                    <div className="text-grayscale-gray7 bg-outline-2 flex size-10 items-center justify-center rounded-full text-lg font-bold">
                      {questionNumber}
                    </div>
                    {showReorderControls &&
                      !isLastQuestion &&
                      !isAnyDragActive && (
                        <button
                          onClick={handleMoveDown}
                          className="absolute bottom-0 left-1/2 flex size-5 -translate-x-1/2 translate-y-[120%] cursor-pointer items-center justify-center p-0 transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 [@media(hover:hover)]:pointer-events-none [@media(hover:hover)]:opacity-0"
                          type="button"
                          aria-label={`Move question ${questionNumber} down`}
                        >
                          <IconChevronDown size={20} stroke={2} />
                        </button>
                      )}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col-reverse gap-2 lg:flex-row">
                    <div className="w-full lg:max-w-[250px] lg:min-w-[175px] lg:flex-1 lg:shrink-0">
                      <Select.Root
                        data-testid={t(
                          questionTemplateType.templateName,
                        ).replace(/\s/g, '')}
                        className="w-full!"
                        isDisabled={isDisplayLanguageMismatch}
                        selectedKey={questionTemplateType.id}
                        onSelectionChange={key => {
                          handleTemplateChange(
                            key == null ? null : String(key),
                          );
                        }}
                      >
                        <Select.Button>
                          <Select.Value />
                        </Select.Button>
                        <Select.Popover>
                          <Select.List>
                            {questionTemplateTypesArray.map(option => (
                              <Select.Item key={option.id} id={option.id}>
                                {t(option.templateName)}
                              </Select.Item>
                            ))}
                          </Select.List>
                        </Select.Popover>
                      </Select.Root>
                    </div>
                    <div className="relative min-w-0 flex-1">
                      <TextInput
                        className={cn(
                          'w-full! max-w-none! [&_.cfa-input-label]:sr-only! [&_input]:w-full! [&_input]:max-w-none!',
                          !hasMultipleQuestions && 'pr-10!',
                        )}
                        data-testid="EnterQuestion"
                        isDisabled={isDisplayLanguageMismatch}
                        errorMessage={
                          showPromptError
                            ? t('TrainingPlans.buildQuizzes.enterQuestion')
                            : ''
                        }
                        maxLength={MAX_V2_QUIZ_TEXT_LENGTH}
                        onChange={handleQuestionName}
                        onBlur={e => {
                          void handleQuestionBlur(e.target.value);
                        }}
                        placeholder={t(
                          'TrainingPlans.buildQuizzes.enterQuestion',
                        )}
                        value={question.question[userLanguage] ?? ''}
                      />
                      {isDisplayLanguageMismatch && (
                        <div
                          aria-hidden
                          data-testid="QuestionPromptHoverTitle"
                          className="pointer-events-auto absolute inset-0 cursor-not-allowed"
                          title={question.question[userLanguage] ?? ''}
                        />
                      )}
                    </div>
                  </div>
                  <div className="mt-2 hidden shrink-0 md:block">
                    <button
                      type="button"
                      aria-label={`Delete question ${questionNumber}`}
                      aria-hidden={!showDeleteTrash}
                      disabled={!showDeleteTrash}
                      data-testid="DiscardDraftQuestion"
                      onClick={() =>
                        isDraft
                          ? onDiscardDraft?.()
                          : setShowDeleteQuestionPopup(true)
                      }
                      className={deleteTrashClassName}
                    >
                      <IconTrash size={24} />
                    </button>
                  </div>
                </div>
                <div className="px-8">
                  <div className="mt-4 text-left">
                    {questionTemplateType.isMultipleChoiceTemplate
                      ? t(
                          'TrainingPlans.buildQuizzes.buildQuestionMultipleChoice',
                        )
                      : t('TrainingPlans.buildQuizzes.buildQuestionTrueFalse')}
                    {showMissingCorrectAnswer && (
                      <div className="text-feedback-error-base mt-2 flex w-full items-center text-xs font-semibold">
                        <IconAlertOctagonFilled className="mr-2 size-4" />
                        {t(
                          'TrainingPlans.buildQuizzes.errorSelectCorrectAnswer',
                        )}
                      </div>
                    )}
                  </div>
                  <BuildQuizzesAnswersV2
                    key={`${question.clientId}-${questionTemplateType.id}`}
                    questionIndex={questionIndex}
                    quiz={quiz}
                    setQuiz={setQuiz}
                    userLanguage={userLanguage}
                    isSaving={isSaving}
                    validationMode={validationMode}
                    trySave={trySave}
                    registerTranslate={registerTranslate}
                    clearTranslate={clearTranslate}
                    onAnswerFieldBlur={() => {
                      scheduleCorrectAnswerErrorCheck(questionIndex);
                    }}
                    onCorrectAnswerSelected={() => {
                      clearCorrectAnswerError();
                    }}
                    isDisplayLanguageMismatch={isDisplayLanguageMismatch}
                  />
                </div>
              </Card.Content>
            </Card.Root>
          </div>
        </div>
      </div>
      <ConfirmationModal
        bodyText={t('Generic.deleteUnnamedObject', {
          object: t('Generic.question').toLowerCase(),
        })}
        headerText={t('Generic.deleteHeader', {
          type: t('Generic.question'),
        })}
        isOpen={showDeleteQuestionPopup}
        onClose={() => setShowDeleteQuestionPopup(false)}
        primaryButtonColor={'primary'}
        primaryButtonHandler={deleteQuestion}
        primaryButtonText={t('Button.delete')}
        primaryButtonVariant="destructive"
        secondaryButtonHandler={() => setShowDeleteQuestionPopup(false)}
        secondaryButtonText={t('Button.cancel')}
      />
    </>
  );
};

export default BuildQuizzesQuestionCardV2;
